import classNames from 'classnames';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useRef,
  useSyncExternalStore,
} from 'react';
import { createPortal } from 'react-dom';

import { UIPortalContext } from '../ui-theme/UITheme';
import { Toast } from './Toast';
import styles from './Toast.module.css';
import { createToastStore, type ToastStore } from './toast-store';
import type {
  ToastApi,
  ToastInstance,
  ToastOptions,
  ToastPosition,
} from './toast-types';

const ToastStoreContext = createContext<ToastStore | null>(null);

const POSITION_CLASS: Record<ToastPosition, string | undefined> = {
  top: styles.top,
  'top-left': styles.topLeft,
  'top-right': styles.topRight,
  bottom: styles.bottom,
  'bottom-left': styles.bottomLeft,
  'bottom-right': styles.bottomRight,
};

const noop = () => undefined;
const NOOP_TOASTS: ToastInstance[] = [];

const noopApi = ((_options: ToastOptions) => '') as ToastApi;
noopApi.isActive = () => false;
noopApi.close = noop;
noopApi.closeAll = noop;
noopApi.update = noop;
noopApi.promise = (promise) => promise;

const noopStore: ToastStore = {
  api: noopApi,
  subscribe: () => noop,
  getSnapshot: () => NOOP_TOASTS,
  pauseTimer: noop,
  resumeTimer: noop,
};

export function useToastStore(): ToastStore {
  return useContext(ToastStoreContext) ?? noopStore;
}

export function useToast(): ToastApi {
  return useToastStore().api;
}

function ToastViewport({ store }: { store: ToastStore }) {
  const toasts = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getSnapshot
  );
  const portalTarget = useContext(UIPortalContext);

  if (toasts.length === 0) {
    return null;
  }

  const byPosition = new Map<ToastPosition, ToastInstance[]>();
  for (const toast of toasts) {
    const group = byPosition.get(toast.position) ?? [];
    group.push(toast);
    byPosition.set(toast.position, group);
  }

  return createPortal(
    [...byPosition.entries()].map(([position, group]) => (
      <div
        key={position}
        className={classNames(styles.viewport, POSITION_CLASS[position])}
      >
        {group.map((toast) => (
          <Toast
            key={toast.id}
            toast={toast}
            onClose={() => store.api.close(toast.id)}
            onPauseTimer={() => store.pauseTimer(toast.id)}
            onResumeTimer={() => store.resumeTimer(toast.id)}
          />
        ))}
      </div>
    )),
    portalTarget
  );
}

export function ToastProvider({ children }: PropsWithChildren) {
  const storeRef = useRef<ToastStore | null>(null);
  if (storeRef.current === null) {
    storeRef.current = createToastStore();
  }
  const store = storeRef.current;

  return (
    <ToastStoreContext.Provider value={store}>
      {children}
      <ToastViewport store={store} />
    </ToastStoreContext.Provider>
  );
}
